/**
 * 방 정보 표시·공유 텍스트 — 언어 이름·통역 방향·입장 정보 복사.
 *
 * 순수 함수만 둔다(테스트가 여기를 겨눈다). DOM·클립보드 부수효과는 호출측에.
 */

/** §16 방향 코드 → 한국어 이름. 모르는 코드는 코드 그대로 보여 준다. */
const LANGUAGE_NAMES: Record<string, string> = {
  ko: '한국어',
  vi: '베트남어',
  en: '영어',
  zh: '중국어',
  ja: '일본어',
  fr: '프랑스어',
  de: '독일어',
  es: '스페인어',
  ru: '러시아어',
}

/**
 * 언어 코드의 표시 이름. 미등록 코드는 **코드를 그대로** 돌려준다 —
 * 빈 문자열이나 '알 수 없음' 으로 뭉개면 방향 표시가 무의미해진다.
 */
export function languageName(code: string): string {
  const key = (code || '').toLowerCase()
  return LANGUAGE_NAMES[key] ?? code
}

/**
 * 통역 방향 표시 — `원언어 → 타깃언어`.
 *
 * 화살표 하나(⇄)만 있는 버튼은 "지금 어느 방향인지" 를 말해 주지 않는다.
 * 회의 중 방향을 잘못 알면 통역이 통째로 헛돌므로(타깃어 발화는 skip 된다)
 * 현재 방향은 **항상 문장으로** 보여야 한다.
 *
 * `compact` 는 자리가 좁은 곳(배지 등)에서 코드 표기(vi → ko)를 쓴다.
 *
 * `bidirectional` 이면 src/dst 는 방향이 아니라 언어 **쌍**이다(§ bidi) —
 * 어느 쪽으로 말해도 반대쪽으로 통역하므로 `A ↔ B` 로 표기한다. 쌍은
 * 순서가 의미 없으나(양방향이므로) 표시는 인자 순서를 그대로 따른다.
 */
export function directionLabel(
  src: string,
  dst: string,
  options: { compact?: boolean; bidirectional?: boolean } = {},
): string {
  const arrow = options.bidirectional ? '↔' : '→'
  if (options.compact) return `${src} ${arrow} ${dst}`
  return `${languageName(src)} ${arrow} ${languageName(dst)}`
}

/** 방향 전환 후의 방향(미리보기 문구용) — 실제 전환은 서버가 한다. */
export function swappedDirectionLabel(
  src: string,
  dst: string,
  options: { compact?: boolean } = {},
): string {
  return directionLabel(dst, src, options)
}

/* ── 입장 정보 복사(§10.0.1) ─────────────────────────────────────────── */

export interface EntryInfoInput {
  /** 불투명 방 ID — URL 에 실리는 축 */
  room: string
  /** 6자리 입장 패스코드 — **URL 에 절대 싣지 않는다** */
  passcode?: string | undefined
  /** opt-in 공개 방이면 직통 링크를 쓴다(§10.2) */
  publicEntry?: boolean | undefined
  /** 공개 방 직통 링크용 view_key(fragment 로만 실린다) */
  viewKey?: string | undefined
  /** 보통 window.location.origin */
  origin: string
  /** S28 회의 이름(opt-in). 비어 있으면 제목 줄에 덧붙이지 않는다 */
  name?: string | undefined
  /** S28 회의 주제(opt-in). 비어 있으면 줄 자체를 만들지 않는다 */
  topic?: string | undefined
}

/**
 * 참가자에게 보낼 초대 문구를 만든다.
 *
 * **불변식: 패스코드는 절대 URL 안에 들어가지 않는다**(§10.0.1). 링크로 만들면
 * 그 순간 두 번째 요소가 브라우저 히스토리·프록시 access 로그·메신저 미리보기
 * 서버로 새어 나간다. 그래서 비공개 방은 "주소 + 패스코드" 를 **따로** 적는다.
 *
 * 공개 방(opt-in)만 직통 링크를 쓴다 — 그때 실리는 것은 fragment 의 view_key
 * 이고, fragment 는 서버로 전송되지 않는 것이 설계된 메커니즘이다(§10.2).
 */
export function buildEntryInfo(input: EntryInfoInput): string {
  const origin = (input.origin || '').replace(/\/$/, '')
  // S28 — 이름이 있으면 제목 줄에 " — {name}" 을 덧붙인다. 둘 다 비면
  // 기존 출력과 **바이트 단위로 동일**해야 한다(회귀 게이트).
  const title = input.name ? `통역 회의 초대 — ${input.name}` : '통역 회의 초대'

  if (input.publicEntry && input.viewKey) {
    // 공개 방 — 직통 링크(fragment 키). 패스코드는 싣지 않는다.
    const link = `${origin}/v/${encodeURIComponent(input.room)}#k=${input.viewKey}`
    const lines = [title]
    if (input.topic) lines.push(`주제: ${input.topic}`)
    lines.push(`참여 링크: ${link}`)
    return lines.join('\n')
  }

  // 비공개 방(기본) — 주소와 패스코드를 **분리**해 적는다.
  const lines = [title]
  if (input.topic) lines.push(`주제: ${input.topic}`)
  lines.push(`접속: ${origin}`)
  if (input.passcode) lines.push(`입장 패스코드: ${input.passcode}`)
  return lines.join('\n')
}

/**
 * 클립보드 복사 — secure context 의 Clipboard API 를 먼저 쓰고,
 * 막히면 execCommand 로 떨어진다(구형·비보안 컨텍스트 대비).
 * 성공 여부를 돌려주므로 호출측이 피드백을 결정한다.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* 폴백으로 내려간다 */
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    // 화면 밖에 두되 포커스는 가능해야 한다(execCommand 전제).
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    ta.setAttribute('readonly', '')
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}
